import type { LabelProps } from "components/Forms/NewLabelWrapper";
import {
  SelectComboField,
  type SelectComboFieldProps,
} from "components/Forms/SelectCombo/SelectCombo";
import {
  aggregationSupportsUnits,
  getUnitsOptionsForParameter,
  isConvertibleParameter,
} from "components/TimeSeries/parameterUnits";
import type React from "react";

export interface ParameterUnitsSelectProps
  extends Omit<SelectComboFieldProps, "options"> {
  parameter: string | null;
  aggregation: string | null;
  label?: LabelProps;
}

/**
 * A dropdown component for selecting units based on the selected parameter and aggregation.
 * Energy parameters allow selection between kWh, Wh, and MWh.
 * Volume parameters allow selection between m3 and litres.
 * Other parameters show a disabled dropdown with their base unit.
 * Non-scalable aggregations (count, data quality) disable the dropdown entirely.
 */
function ParameterUnitsSelect(
  props: ParameterUnitsSelectProps,
): React.ReactElement {
  const { parameter, aggregation, disabled, ...rest } = props;

  const supportsUnits = aggregation
    ? aggregationSupportsUnits(aggregation)
    : true;
  const options =
    parameter && supportsUnits ? getUnitsOptionsForParameter(parameter) : [];
  const isConvertible = parameter ? isConvertibleParameter(parameter) : false;

  // Disable the dropdown if:
  // 1. The parameter is not convertible (temp, power, etc.)
  // 2. The aggregation doesn't support units (count, data quality)
  // 3. The disabled prop is true
  const isDisabled = disabled || !isConvertible || !supportsUnits;

  return (
    <SelectComboField
      {...rest}
      options={options}
      disabled={isDisabled}
      clearable={false}
    />
  );
}

export default ParameterUnitsSelect;
